<template>
    <div>
        <Carousel :opts="{ direction: 'rtl', loop: true }" class="relative w-full h-full" @init-api="(val) => (main = val)">
            <CarouselContent>
                <CarouselItem v-for="(item, key) in galleryArray" :key @click="selectImage(item)">
                    <Image :desktop="item" :loading="key === 0 ? 'eager' : 'lazy'" format="webp" class="w-full h-full" preset="hero" />
                </CarouselItem>
            </CarouselContent>
        </Carousel>
        <div v-gsap.desktop.parallax.slower-8 class="w-full">
            <Carousel
                :opts="{ direction: 'rtl', align: 'center', loop: true }"
                class="lg:bottom-4 lg:left-full lg:absolute w-full lg:max-w-xs lg:-translate-x-1/2"
                @init-api="(val) => (thumbnail = val)"
            >
                <CarouselContent class="flex gap-1 ml-0 p-2" :class="galleryArray.length > 4 ? '' : 'justify-center'">
                    <CarouselItem
                        v-for="(item, key) in galleryArray"
                        :key
                        class="pt-1 w-full h-full aspect-square cursor-pointer basis-1/4"
                        @click="onThumbClick(key)"
                    >
                        <Glass
                            blur="xs"
                            content-class="w-full h-full"
                            class="w-full h-full"
                            :class="key === selectedIndex ? 'border-primary/25' : ''"
                        >
                            <Image
                                loading="eager"
                                format="webp"
                                :quality="10"
                                sizes="150px"
                                :desktop="item"
                                class="w-full h-full"
                                :class="key === selectedIndex ? 'bg-primary/5' : 'opacity-50'"
                            />
                        </Glass>
                    </CarouselItem>
                </CarouselContent>
            </Carousel>
        </div>
        <LazyPageProductCarouselDialog v-model:open="isOpen" :image="selectedImage" />
    </div>
</template>

<script lang="ts" setup>
import type { CarouselApi } from "@/components/ui/carousel";
import { watchOnce } from "@vueuse/core";
import { Glass } from "@/components/ui/glass";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

interface Props {
    gallery?: APIMediaCollection;
}

const props = defineProps<Props>();

const galleryArray = computed(() => Object.values(props.gallery ?? {}));

const main = ref<CarouselApi>();
const thumbnail = ref<CarouselApi>();
const selectedIndex = ref(0);
const isOpen = ref<boolean>();
const selectedImage = ref<APIMediaCollection | undefined>();

// Use route state to control modal so browser back closes it
const router = useRouter();
const route = useRoute();

function onSelect() {
    if (!main.value || !thumbnail.value) return;
    selectedIndex.value = main.value.selectedScrollSnap();
    thumbnail.value.scrollTo(main.value.selectedScrollSnap());
}

function onThumbClick(index: number) {
    if (!main.value || !thumbnail.value) return;
    main.value.scrollTo(index);
}

function selectImage(image: APIMediaCollection) {
    selectedImage.value = image;
    // Push a history state flag so back button will close the modal
    if (route.query.modal !== "image") {
        router.push({ query: { ...route.query, modal: "image" } });
    }
    isOpen.value = true;
}

watchOnce(main, (newMain) => {
    if (!newMain) return;

    onSelect();
    newMain.on("select", onSelect);
    newMain.on("reInit", onSelect);
});

// Keep dialog open state in sync with route query
watch(
    () => route.query.modal,
    (val) => {
        const shouldOpen = val === "image";
        isOpen.value = shouldOpen;
        if (!shouldOpen) {
            selectedImage.value = undefined;
        }
    },
    { immediate: true },
);

// When dialog closes (via UI), step back once to remove the modal state
watch(isOpen, (open, prev) => {
    if (prev && !open && route.query.modal === "image") {
        router.back();
    }
});
</script>

<style></style>
